/**
 * Status-first error mapping for the lesson-booking POST. Pure — no React,
 * no network.
 *
 * Branch on `result.httpStatus`, NEVER on `error.code` — `toMemberLessonBookingHttpError.ts`
 * maps 402 / 409 / 422 ALL to `BUSINESS_RULE_VIOLATION`; only a bay conflict
 * carries `CONFLICT`, and that check happens ONLY inside the 409 branch.
 * Never substring-match a server message — messages are copy, not a wire
 * contract, and can change under this code without warning.
 *
 * @module pages/lessons/lessonBookingErrors
 */

import { ErrorCodes } from '~/lib/apiClient/types';
import type { ApiErrorResult } from '~/lib/apiClient/types';
import { LESSON_DURATION_LIMIT_MESSAGE } from '@butter/library/lessons';

export type LessonBookingRecovery =
  | 'pick-instructor' // → step 0
  | 'pick-time' // → step 1
  | 'pick-bay' // → step 1 (the bay picker lives on Lesson Time)
  | 'add-payment' // → swap to the inline CardEntryForm / choose another card
  | 'requote' // → refresh the price (account credit changed since the quote)
  | 'retry'
  | 'none';

export interface LessonBookingFailure {
  /** What the member reads. The SERVER's message wherever the server wrote one. */
  message: string;
  primary: { label: string; recovery: LessonBookingRecovery };
  secondary?: { label: string; recovery: LessonBookingRecovery };
  /** Field-level detail for a 400 — `error.details.errors: { field, message }[]`. */
  fieldErrors?: { field: string; message: string }[];
  /** A 402 is safe to retry — the booking is deleted and the minutes restored. */
  retryable: boolean;
}

/**
 * Frozen module constant — the `>= 500` / transport-failure (`httpStatus: 0`) row.
 * Annotated on the literal, before `Object.freeze`, because `Object.freeze`'s own
 * generic inference does not pick up the outer contextual type — without this the
 * literal's `recovery: 'retry'` widens to `string` and fails against
 * `LessonBookingRecovery`.
 */
const GENERIC_LESSON_FAILURE_LITERAL: LessonBookingFailure = {
  message: 'Something went wrong booking your lesson. Please try again.',
  primary: { label: 'Try again', recovery: 'retry' },
  retryable: true,
};
export const GENERIC_LESSON_FAILURE: LessonBookingFailure = Object.freeze(
  GENERIC_LESSON_FAILURE_LITERAL,
);

/** Frozen module constant — the pre-submit duration-limit refusal, rendered before Confirm is even reachable. */
const DURATION_LIMIT_FAILURE_LITERAL: LessonBookingFailure = {
  message: LESSON_DURATION_LIMIT_MESSAGE,
  primary: { label: 'Pick another time', recovery: 'pick-time' },
  retryable: false,
};
export const LESSON_DURATION_LIMIT_FAILURE: LessonBookingFailure = Object.freeze(
  DURATION_LIMIT_FAILURE_LITERAL,
);

interface ValidationErrorDetails {
  errors?: { field: string; message: string }[];
}

interface AdvanceBookingExceededDetails {
  lastBookableDate?: string;
  advanceBookingDays?: number;
}

/**
 * Map a lesson-booking create failure onto what the member reads and can do
 * next. See `apps/api/src/api/public/v1/controllers/lessonBookingControllers/toMemberLessonBookingHttpError.ts`
 * for the server side of this contract.
 */
export function toLessonBookingFailure(result: ApiErrorResult): LessonBookingFailure {
  switch (result.httpStatus) {
    case 400: {
      const details = result.error?.details as ValidationErrorDetails | null | undefined;
      return {
        message: result.message,
        primary: { label: 'Start over', recovery: 'pick-time' },
        retryable: false,
        fieldErrors: details?.errors,
      };
    }

    case 402:
      // The provider's own decline text, rendered VERBATIM — not client-authored copy.
      return {
        message: result.message,
        primary: { label: 'Try another card', recovery: 'add-payment' },
        retryable: true,
      };

    case 404:
      return {
        message: result.message,
        primary: { label: 'Choose an instructor', recovery: 'pick-instructor' },
        retryable: false,
      };

    case 409: {
      // `error.code` is read only here — to tell a bay just taken (CONFLICT) and the
      // member's credit no longer covering the lesson (`credit_balance_changed`, credits
      // D30) from everything else 409 carries (BUSINESS_RULE_VIOLATION): instructor
      // unavailable AND package-not-covered, indistinguishable to the client.
      if (result.error?.code === 'credit_balance_changed') {
        // Nothing was booked or charged. Re-quote: the card step comes back.
        return {
          message: result.message,
          primary: { label: 'Review price', recovery: 'requote' },
          retryable: true,
        };
      }
      if (result.error?.code === ErrorCodes.CONFLICT) {
        return {
          message: result.message,
          primary: { label: 'Choose another bay', recovery: 'pick-bay' },
          secondary: { label: 'Pick another time', recovery: 'pick-time' },
          retryable: true,
        };
      }
      return {
        message: result.message,
        primary: { label: 'Choose another instructor', recovery: 'pick-instructor' },
        secondary: { label: 'Pick another time', recovery: 'pick-time' },
        retryable: false,
      };
    }

    case 422: {
      const details = result.error?.details as AdvanceBookingExceededDetails | null | undefined;
      if (details?.lastBookableDate) {
        return {
          message: `${result.message} You can book up to ${details.lastBookableDate}.`,
          primary: { label: 'Pick another date', recovery: 'pick-time' },
          retryable: false,
        };
      }
      // The 120-minute ceiling (or any other duration/hours refusal) is already
      // interpolated INTO `result.message` server-side — it is NOT in `details`.
      return {
        message: result.message,
        primary: { label: 'Pick another time', recovery: 'pick-time' },
        retryable: false,
      };
    }

    default:
      return GENERIC_LESSON_FAILURE;
  }
}
